/**
 * Durable run_pipelines "fragment" types (REST /mcp/projects/.../fragments).
 *
 * A fragment is a named, reusable template of run_pipelines step dicts stored
 * per-project in review_mcp so the LLM agent can invoke it by name. The reMap
 * "Save view" flow dual-writes one of these alongside the graph-view-state
 * save; the shapes here mirror the service's define/list/get returns.
 */

/** Body for `POST /mcp/projects/{id}/fragments`. */
export interface DefineFragmentRequest {
  name: string;
  /** run_pipelines step dicts; kept opaque (validated server-side). */
  steps: unknown[];
  params?: string[];
  required?: string[];
  output?: string | null;
  doc?: string;
}

/**
 * Mirrors `service.define_fragment`'s return. `steps` is the stored step
 * *count*, not the template; the template comes back from `GET .../{name}`.
 */
export interface DefineFragmentResponse {
  ok: boolean;
  name: string;
  params: string[];
  required: string[];
  /**
   * The step COUNT, not the template — the define surface echoes a summary.
   * Fetch the template itself with `getFragment` (GET .../fragments/{name}).
   */
  steps: number;
}

/**
 * Mirrors review_mcp's `PipelineType`. DEFAULT is a built-in macro (never
 * listed); USER_CREATED is the project's own fragment (editable); GOVERNED is
 * one of its analytical definitions (read-only).
 */
export const PIPELINE_TYPE = {
  DEFAULT: 'DEFAULT',
  USER_CREATED: 'USER_CREATED',
  GOVERNED: 'GOVERNED',
} as const;

export type PipelineType = (typeof PIPELINE_TYPE)[keyof typeof PIPELINE_TYPE];

interface FragmentListItemBase {
  name: string;
  params: string[];
  required: string[];
  doc: string;
}

/** A saved fragment in the list (name / params / required / doc). */
export interface UserFragmentListItem extends FragmentListItemBase {
  type: typeof PIPELINE_TYPE.USER_CREATED;
}

/** A runtime `{param}` a governed pipeline binds. */
export interface GovernedRuntimeParam {
  name: string;
  type: string;
  description?: string;
}

/** A governed pipeline in the list; its `params` and `required` are empty. */
export interface GovernedFragmentListItem extends FragmentListItemBase {
  type: typeof PIPELINE_TYPE.GOVERNED;
  question: string;
  runtime_params: GovernedRuntimeParam[];
}

/** One entry of the fragments list. */
export type FragmentListItem = UserFragmentListItem | GovernedFragmentListItem;

/** Mirrors `service.list_fragments`'s return. */
export interface ListFragmentsResponse {
  ok: boolean;
  fragments: FragmentListItem[];
}

/** The full stored FragmentDef (from `GET .../fragments/{name}`). */
export interface UserFragmentDetail {
  type: typeof PIPELINE_TYPE.USER_CREATED;
  name: string;
  params: string[];
  steps: unknown[];
  required: string[];
  output: string | null;
  doc: string;
}

/** A governed pipeline's list entry plus its `steps`; read-only. */
export interface GovernedFragmentDetail extends GovernedFragmentListItem {
  steps: unknown[];
}

export type FragmentDetail = UserFragmentDetail | GovernedFragmentDetail;
